/**
 * Zap WebGL Game Manager
 *
 * @package ZapCompetitionGames
 * @version 1.0.0
 */

/**
 * Manages Unity WebGL loader script and instance creation.
 */
class UnityLoader {
	constructor(buildUrl, fileNames) {
		this.buildUrl = buildUrl;
		this.fileNames = fileNames;
		this.loaderPromise = null;
		this.instance = null;
	}

	/**
	 * Loads the Unity loader script (singleton pattern).
	 * @returns {Promise<void>}
	 */
	loadScript() {
		if (this.loaderPromise) {
			return this.loaderPromise;
		}

		const loaderUrl = `${this.buildUrl}/${this.fileNames.loader}`;

		this.loaderPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = loaderUrl;
			script.onload = resolve;
			script.onerror = () => reject(new Error('Failed to load Unity loader script'));
			document.body.appendChild(script);
		});

		return this.loaderPromise;
	}

	/**
	 * Creates a Unity instance on the specified canvas.
	 * @param {HTMLCanvasElement} canvas
	 * @param {Function} onProgress
	 * @returns {Promise<Object>}
	 */
	async createInstance(canvas, onProgress) {
		await this.loadScript();

		const config = {
			arguments: [],
			dataUrl: `${this.buildUrl}/${this.fileNames.data}`,
			frameworkUrl: `${this.buildUrl}/${this.fileNames.framework}`,
			codeUrl: `${this.buildUrl}/${this.fileNames.wasm}`,
			streamingAssetsUrl: `${this.buildUrl}/StreamingAssets`,
			companyName: 'ZapCompetitions',
			productName: 'WebGLGame',
			productVersion: '1.0',
		};

		// createUnityInstance is provided by Unity's loader script.
		this.instance = await window.createUnityInstance(canvas, config, onProgress || function () {});
		return this.instance;
	}

	/**
	 * Sends a message to Unity.
	 * @param {string} objectName
	 * @param {string} methodName
	 * @param {*} data
	 */
	sendMessage(objectName, methodName, data) {
		if (!this.instance) {
			console.warn('[UnityLoader] Instance not initialized');
			return;
		}

		const payload = typeof data === 'object' ? JSON.stringify(data) : data;
		this.instance.SendMessage(objectName, methodName, payload);
	}

	/**
	 * Quits the Unity instance.
	 * @returns {Promise<void>}
	 */
	async quit() {
		if (this.instance?.Quit) {
			await this.instance.Quit();
		}
		this.instance = null;
	}

	get isRunning() {
		return this.instance !== null;
	}
}

/**
 * Manages ticket state and UI rendering.
 */
class TicketManager {
	constructor(tickets, listElement) {
		this.allTickets = tickets;
		this.listElement = listElement;
		this.remaining = new Set();
		this.onAllClaimed = null;
	}

	reset() {
		this.remaining = new Set(this.allTickets.map((t) => t.ticket_number));
		this.render();
	}

	claim(ticketNumber) {
		const num = parseInt(ticketNumber, 10);

		if (!Number.isFinite(num) || !this.remaining.has(num)) {
			return false;
		}

		this.remaining.delete(num);
		this.render();

		if (this.remaining.size === 0 && typeof this.onAllClaimed === 'function') {
			this.onAllClaimed();
		}

		return true;
	}

	claimAll() {
		this.remaining.clear();
		this.render();

		if (typeof this.onAllClaimed === 'function') {
			this.onAllClaimed();
		}
	}

	/** JUST FOR DEMO PURPOSES **/
	render() {
		if (!this.listElement) {
			return;
		}

		this.listElement.innerHTML = '';

		this.allTickets.forEach((ticket) => {
			const li = document.createElement('li');
			li.textContent = ticket.ticket_number;

			const isClaimed = !this.remaining.has(ticket.ticket_number);

			Object.assign(li.style, {
				padding: '12px',
				textAlign: 'center',
				borderRadius: '6px',
				fontWeight: '600',
				border: '2px solid',
				background: isClaimed ? '#d4edda' : '#e8eaf6',
				borderColor: isClaimed ? '#28a745' : '#667eea',
				color: isClaimed ? '#28a745' : '#667eea',
			});

			if (isClaimed) {
				li.classList.add('ticket-claimed');
			}

			this.listElement.appendChild(li);
		});
	}
	/** //// //// //// JUST FOR DEMO PURPOSES **/

	get remainingCount() {
		return this.remaining.size;
	}

	get allClaimed() {
		return this.remaining.size === 0;
	}
}

/**
 * Main controller for the WebGL game experience.
 */
class WebGLGameManager {
	/**
	 * @param wrapperId
	 * @param {Object} config
	 */
	constructor(wrapperId, config) {
		this.config = config;
		this.isRunning = false;

		// Get wrapper from ID passed in config.
		const wrapper = document.getElementById(wrapperId);

		if (!wrapper) {
			console.error('[WebGLGameManager] Wrapper not found:', wrapperId);
			return;
		}

		// DOM elements - queried from wrapper.
		this.elements = {
			wrapper,
			canvas:     wrapper.querySelector('.zap-game-canvas'),
			container:  wrapper.querySelector('.zap-game-container'),
			complete:   wrapper.querySelector('.zap-game-complete'),
			ticketList: wrapper.querySelector('.zap-game-ticket-list'),
			btnRestart: wrapper.querySelector('.zap-game-btn-restart'),
		};

		// Unity loader.
		this.unity = new UnityLoader(config.buildUrl, config.buildFiles || {
			loader: 'game.loader.js',
			data: 'game.data',
			framework: 'game.framework.js',
			wasm: 'game.wasm',
		});

		// Ticket manager.
		this.tickets = new TicketManager(config.gameData || [], this.elements.ticketList);
		this.tickets.onAllClaimed = () => this.handleAllTicketsClaimed();

		this.bindEvents();
	}

	bindEvents() {
		this.elements.btnClose?.addEventListener('click', () => this.close());
		this.elements.btnRestart?.addEventListener('click', () => this.restart());

		window.addEventListener('unity-claimed-ticket', (e) => {
			if (e?.detail?.ticketNumber) {
				this.tickets.claim(e.detail.ticketNumber);
			}
		});

		window.addEventListener('unity-claimed-all-tickets', () => {
			this.tickets.claimAll();
		});
	}

	show(el) {
		if (el) {
			el.style.display = 'block';
		}
	}

	hide(el) {
		if (el) {
			el.style.display = 'none';
		}
	}

	async init() {
		if (this.isRunning) {
			return;
		}

		this.isRunning = true;
		this.tickets.reset();

		try {
			await this.unity.createInstance(
				this.elements.canvas,
			);

			// Send game data to Unity.
			const bridge = this.config.unityBridge || 'WebUnityBridge';
			const payload = {
				data: this.config.gameData,
				theme: this.config.theme || {},
			};
			this.unity.sendMessage(bridge, 'SetGameData', payload);
		} catch (error) {
			console.error('[WebGLGameManager] Init error:', error);
			this.isRunning = false;
		}
	}

	async close() {
		try {
			await this.unity.quit();
		} catch (error) {
			console.warn('[WebGLGameManager] Close warning:', error);
		}

		this.isRunning = false;
		this.hide(this.elements.container);
	}

	async restart() {
		window.location.reload();
	}

	handleAllTicketsClaimed() {
		if (!this.isRunning) {
			return;
		}

		this.show(this.elements.complete);
	}
}

// Export for module usage.
export { WebGLGameManager, UnityLoader, TicketManager };

// Expose globally for template usage.
window.WebGLGameManager = WebGLGameManager;
